import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Paper,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TextFieldDataCustom from 'components/shared/textfiel_data_custom';

const memberRoles = ['Presidente', 'Vice', 'Secretário Executivo'];
const bondTypes = ['Poder público', 'Sociedade civil'];

interface Member {
  role: string;
  bond: string;
  institution: string;
  name: string;
  start: string;
  end: string;
  phone: string;
  mobile: string;
  email: string;
}

export interface Step2CmdrsData {
  members: Member[];
}
interface Step2Props {
  hasCmdrs: boolean | null;
  disabled?: boolean;
  onNext: () => void;
  onBack: () => void;
  onChange: (data: Step2CmdrsData) => void;
  data: Step2CmdrsData;
}

const emptyMember: Member = {
  role: '',
  bond: '',
  institution: '',
  name: '',
  start: '',
  end: '',
  phone: '',
  mobile: '',
  email: '',
};

export const useStep2Form = (
  initialMembers: Member[],
  deps: { onChange: (data: Step2CmdrsData) => void; onNext: () => void; hasCmdrs?: boolean | null; disabled?: boolean },
) => {
  const [members, setMembers] = useState<Member[]>(initialMembers || []);
  const [form, setForm] = useState<Member>(emptyMember);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    setMembers(initialMembers || []);
  }, [initialMembers]);

  useEffect(() => {
    deps.onChange({ members });
    // eslint-disable-next-line
  }, [members]);

  const handleChange = (field: keyof Member, value: string) => {
    setForm({ ...form, [field]: value });
  };

  const handleAddOrEdit = () => {
    const validation = validateMember(form);
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;

    if (editIdx !== null) {
      const updated = [...members];
      updated[editIdx] = form;
      setMembers(updated);
      setEditIdx(null);
    } else {
      setMembers([...members, form]);
    }
    setForm(emptyMember);
    setErrors({});
  };

  const handleEdit = (idx: number) => {
    setForm(members[idx]);
    setEditIdx(idx);
    setErrors({});
  };

  const handleRemove = (idx: number) => {
    setMembers(members.filter((_, i) => i !== idx));
    if (editIdx === idx) {
      setForm(emptyMember);
      setEditIdx(null);
      setErrors({});
    }
  };

  const handleSubmit = () => {
    if (deps.hasCmdrs && members.length === 0) {
      setErrors({ members: 'Adicione pelo menos um membro' });
      return;
    }
    setErrors({});
    deps.onNext();
  };

  return {
    members,
    form,
    editIdx,
    errors,
    handleChange,
    handleAddOrEdit,
    handleEdit,
    handleRemove,
    handleSubmit,
    setForm,
    setErrors,
    setEditIdx,
    setMembers,
  };
};

const Step2: React.FC<Step2Props> = ({ hasCmdrs, disabled, onNext, onBack, onChange, data }) => {
  const {
    members,
    form,
    editIdx,
    errors,
    handleChange,
    handleAddOrEdit,
    handleEdit,
    handleRemove,
    handleSubmit,
    setForm,
    setErrors,
    setEditIdx,
  } = useStep2Form(data?.members || [], { onChange, onNext, hasCmdrs, disabled });
  const camposObrigatoriosPreenchidos = !hasCmdrs || members.length > 0;

  const formatDateBr = (dateStr: string) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  // Função para aplicar máscara de telefone fixo
  const maskPhone = (value: string) => {
    const v = value.replace(/\D/g, '').slice(0, 10);
    if (v.length <= 2) return v;
    if (v.length <= 6) return `(${v.slice(0, 2)}) ${v.slice(2)}`;
    return `(${v.slice(0, 2)}) ${v.slice(2, 6)}-${v.slice(6)}`;
  };

  // Função para aplicar máscara de celular
  const maskMobile = (value: string) => {
    const v = value.replace(/\D/g, '').slice(0, 11);
    if (v.length <= 2) return v;
    if (v.length <= 7) return `(${v.slice(0, 2)}) ${v.slice(2)}`;
    return `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
  };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Diretoria Executiva do CMDRS
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="dense" required>
              <InputLabel>Função</InputLabel>
              <Select
                value={form.role}
                onChange={e => handleChange('role', e.target.value)}
                error={!!errors.role}
                label="Função"
                disabled={!hasCmdrs || disabled}
              >
                {memberRoles.map(role => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </Select>
              {errors.role && <Typography color="error">{errors.role}</Typography>}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="dense" required>
              <InputLabel>Tipo de vínculo</InputLabel>
              <Select
                value={form.bond}
                onChange={e => handleChange('bond', e.target.value)}
                error={!!errors.bond}
                label="Tipo de vínculo"
                disabled={!hasCmdrs || disabled}
              >
                {bondTypes.map(bond => (
                  <MenuItem key={bond} value={bond}>{bond}</MenuItem>
                ))}
              </Select>
              {errors.bond && <Typography color="error">{errors.bond}</Typography>}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Nome da instituição"
              value={form.institution}
              onChange={e => handleChange('institution', e.target.value.toUpperCase())}
              error={!!errors.institution}
              helperText={errors.institution}
              fullWidth
              margin="dense"
              required
              disabled={!hasCmdrs || disabled}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Nome completo do membro"
              value={form.name}
              onChange={e => handleChange('name', e.target.value.toUpperCase())}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              margin="dense"
              required
              disabled={!hasCmdrs || disabled}
            />
          </Grid>
          <Grid item xs={6} md={6}>
            <TextFieldDataCustom
              label="Início da vigência"
              value={form.start}
              onChange={e => handleChange('start', e.target.value)}
              error={!!errors.start}
              helperText={errors.start}
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
              required
              disabled={!hasCmdrs || disabled}
            />
          </Grid>
          <Grid item xs={6} md={6}>
            <TextFieldDataCustom
              label="Fim da vigência"
              value={form.end}
              onChange={e => handleChange('end', e.target.value)}
              error={!!errors.end}
              helperText={errors.end}
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
              required
              disabled={!hasCmdrs || disabled}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Telefone fixo"
              value={form.phone}
              onChange={e => {
                const masked = maskPhone(e.target.value);
                setForm({ ...form, phone: masked });
              }}
              inputProps={{ maxLength: 14 }} // (99) 9999-9999 tem 14 caracteres
              error={!!errors.phone}
              helperText={errors.phone}
              fullWidth
              margin="dense"
              disabled={!hasCmdrs || disabled}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Celular"
              value={form.mobile}
              onChange={e => {
                const masked = maskMobile(e.target.value);
                setForm({ ...form, mobile: masked });
              }}
              inputProps={{ maxLength: 15 }} // (99) 99999-9999 tem 15 caracteres
              error={!!errors.mobile}
              helperText={errors.mobile}
              fullWidth
              margin="dense"
              disabled={!hasCmdrs || disabled}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="E-mail"
              type="email"
              value={form.email}
              onChange={e => handleChange('email', e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              margin="dense"
              required
              disabled={!hasCmdrs || disabled}
            />
          </Grid>
        </Grid>

        <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
          {editIdx !== null && (
            <Button
              onClick={() => {
                setForm(emptyMember);
                setEditIdx(null);
                setErrors({});
              }}
              color="inherit"
            >
              Cancelar
            </Button>
          )}
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#0048b6',
              color: '#fff',
              '&:hover': { backgroundColor: '#003080' },
            }}
            onClick={handleAddOrEdit}
            disabled={!hasCmdrs || disabled}
            startIcon={editIdx === null ? <AddIcon /> : undefined}
          >
            {editIdx !== null ? 'Salvar alterações' : 'Adicionar membro'}
          </Button>
        </Box>
      </Paper>

      {/* Lista de membros */}
      {members.length > 0 && (
        <Paper
          elevation={1}
          sx={{
            p: 1,
            mb: 1,
            background: '#f5f5f5',
            opacity: !hasCmdrs || disabled ? 0.6 : 1,
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={1}><Typography variant="caption" color="text.secondary">Função</Typography></Grid>
            <Grid item xs={1}><Typography variant="caption" color="text.secondary">Vínculo</Typography></Grid>
            <Grid item xs={2}><Typography variant="caption" color="text.secondary">Instituição</Typography></Grid>
            <Grid item xs={2}><Typography variant="caption" color="text.secondary">Nome</Typography></Grid>
            <Grid item xs={1}><Typography variant="caption" color="text.secondary">Início</Typography></Grid>
            <Grid item xs={1}><Typography variant="caption" color="text.secondary">Fim</Typography></Grid>
            <Grid item xs={1}><Typography variant="caption" color="text.secondary">Telefone</Typography></Grid>
            <Grid item xs={1}><Typography variant="caption" color="text.secondary">Celular</Typography></Grid>
            <Grid item xs={2}><Typography variant="caption" color="text.secondary">E-mail</Typography></Grid>
            <Grid item xs={1}><Typography variant="caption" color="text.secondary"></Typography></Grid>
          </Grid>
        </Paper>
      )}

      {members.map((m, idx) => (
        <React.Fragment key={idx}>
          <Paper
            elevation={1}
            sx={{
              p: 2,
              mb: 2,
              position: 'relative',
              opacity: !hasCmdrs || disabled ? 0.6 : 1,
            }}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={1}><Typography variant="body2">{m.role}</Typography></Grid>
              <Grid item xs={1}><Typography variant="body2">{m.bond}</Typography></Grid>
              <Grid item xs={2}><Typography variant="body2">{m.institution}</Typography></Grid>
              <Grid item xs={2}><Typography variant="body2">{m.name}</Typography></Grid>
              <Grid item xs={1}><Typography variant="body2">{formatDateBr(m.start)}</Typography></Grid>
              <Grid item xs={1}><Typography variant="body2">{formatDateBr(m.end)}</Typography></Grid>
              <Grid item xs={1}><Typography variant="body2">{m.phone}</Typography></Grid>
              <Grid item xs={1}><Typography variant="body2">{m.mobile}</Typography></Grid>
              <Grid item xs={2}>
                <Tooltip title={m.email}>
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 180,
                      display: 'block',
                      cursor: 'pointer',
                    }}
                  >
                    {m.email}
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <IconButton
                  color="primary"
                  onClick={() => handleEdit(idx)}
                  disabled={!hasCmdrs || disabled}
                  aria-label="Editar membro"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={() => handleRemove(idx)}
                  disabled={!hasCmdrs || disabled}
                  aria-label="Remover membro"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
          {idx < members.length - 1 && <Divider sx={{ my: 2 }} />}
        </React.Fragment>
      ))}

      <Divider sx={{ my: 2 }} />

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onBack}
          disabled={disabled}
        >
          Voltar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!camposObrigatoriosPreenchidos || !hasCmdrs || disabled}
        >
          Salvar e avançar
        </Button>
      </Box>
    </Box>
  );
};

// Pure helpers exported for testing
export const validateMember = (data: Member) => {
  const newErrors: { [key: string]: string } = {};
  if (!data.role) newErrors.role = 'Campo obrigatório';
  if (!data.bond) newErrors.bond = 'Campo obrigatório';
  if (!data.institution) newErrors.institution = 'Campo obrigatório';
  if (!data.name) newErrors.name = 'Campo obrigatório';
  if (!data.start) newErrors.start = 'Campo obrigatório';
  if (!data.end) newErrors.end = 'Campo obrigatório';
  if (data.phone && !/^\(?\d{2}\)?\s?\d{4,5}-?\d{4}$/.test(data.phone)) newErrors.phone = 'Telefone inválido';
  if (data.mobile && !/^\(?\d{2}\)?\s?\d{4,5}-?\d{4}$/.test(data.mobile)) newErrors.mobile = 'Celular inválido';
  if (!data.email) {
    newErrors.email = 'Campo obrigatório';
  } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(data.email)) {
    newErrors.email = 'E-mail inválido';
  }
  if (data.start && data.end && data.start >= data.end) {
    newErrors.end = 'A data de fim deve ser posterior à data de início';
  }
  return newErrors;
};

export const applyAddOrEdit = (members: Member[], form: Member, editIdx: number | null) => {
  if (editIdx !== null && editIdx >= 0 && editIdx < members.length) {
    const updated = [...members];
    updated[editIdx] = form;
    return { members: updated, editIdx: null, form: emptyMember };
  }
  return { members: [...members, form], editIdx: null, form: emptyMember };
};

export const removeMember = (members: Member[], idx: number) => {
  return members.filter((_, i) => i !== idx);
};

export const changeForm = (form: Member, field: keyof Member, value: string): Member => ({
  ...form,
  [field]: value,
});

export const getEditState = (members: Member[], idx: number) => ({
  form: members[idx],
  editIdx: idx,
  errors: {},
});

export const canSubmit = (members: Member[], hasCmdrs?: boolean | null) => {
  if (hasCmdrs && members.length === 0) return { members: 'Adicione pelo menos um membro' };
  return {};
};

export const formatDateBrExport = (dateStr: string) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

export const maskPhoneExport = (value: string) => {
  const v = value.replace(/\D/g, '').slice(0, 10);
  if (v.length <= 2) return v;
  if (v.length <= 6) return `(${v.slice(0, 2)}) ${v.slice(2)}`;
  return `(${v.slice(0, 2)}) ${v.slice(2, 6)}-${v.slice(6)}`;
};

export const maskMobileExport = (value: string) => {
  const v = value.replace(/\D/g, '').slice(0, 11);
  if (v.length <= 2) return v;
  if (v.length <= 7) return `(${v.slice(0, 2)}) ${v.slice(2)}`;
  return `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
};

export default Step2;
